import { Almanac } from "protos/robot_syncer/plant_profile";
import { Formula } from "protos/almanac/almanac";
import { TFunction } from "i18next";

export interface BandBounds {
  lower: number;
  upper: number;
}

const DEFAULT_MAX_RADIUS_MM = 100;
const DOMAIN_STEP_MM = 1;
export const DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX = 5;
export const MIN_SHOOT_TIME_FINE_TUNE_INDEX = 1;
export const MAX_SHOOT_TIME_FINE_TUNE_INDEX = 10;

// Valid sizes are: positive, finite, and _strictly ascending_.
// All other functions in this file depend on valid sizes coming in as inputs
const areSizesValid = (sizes: readonly number[]): boolean => {
  if (sizes.length === 0) {
    return false;
  }
  for (let i = 0; i < sizes.length; i++) {
    if (!Number.isFinite(sizes[i]) || sizes[i]! <= 0) {
      return false;
    }
    if (i > 0 && sizes[i]! <= sizes[i - 1]!) {
      return false;
    }
  }
  return true;
};

export const getBandCount = (sizes: readonly number[]): number =>
  sizes.length + 1;

const getDefaultMaxRadiusMm = (sizes: readonly number[]): number => {
  const lastThreshold = sizes.at(-1);
  if (lastThreshold === undefined) {
    return DEFAULT_MAX_RADIUS_MM;
  }
  return Math.max(DEFAULT_MAX_RADIUS_MM, lastThreshold * 1.5); // make sure the final band has some breathing room
};

/** True when size boundaries and formulas are mutually consistent. */
export const isValidAlmanac = (
  almanac: Almanac | undefined
): almanac is Almanac => {
  if (!almanac) {
    return false;
  }
  const { sizeBoundariesMm, formulas } = almanac;
  return (
    areSizesValid(sizeBoundariesMm) &&
    formulas.length === sizeBoundariesMm.length + 1
  );
};

export const getBandBounds = (
  sizes: readonly number[],
  maxRadiusMm: number
): BandBounds[] => {
  const bounds: BandBounds[] = [];
  for (let i = 0; i < getBandCount(sizes); i++) {
    const lower = i === 0 ? 0 : sizes[i - 1]!;
    const upper = i < sizes.length ? sizes[i]! : maxRadiusMm;
    bounds.push({ lower, upper });
  }
  return bounds;
};

/**
 * Which fine-tune factor scales the formula.
 * - `2.5`: category-template / plant-profile almanacs, which ignore
 *   the stored fine-tune fields and read the quadratic curve at a given index.
 * - `legacy`: robot almanacs still running the pre-2.5 formula, scaled by the
 *   fine-tune values stored on the formula itself.
 */
export type ShootTimeFormulaVersion = "2.5" | "legacy";
export type LaserTimeOptions =
  | { formulaVersion: "2.5"; fineTuneIndex?: number }
  | { formulaVersion: "legacy"; fineTuneIndex?: never };

/** Quadratic fine-tune curve: F(1)=0.5, F(5)=1, and F(10)=2. */
export const getFineTuneFactor = (fineTuneIndex: number): number =>
  fineTuneIndex ** 2 / 120 + (3 * fineTuneIndex) / 40 + 5 / 12;

/** Pre-2.5 curve: F = 1 + (FTI - 5) × FTM, both read off the formula. */
export const getLegacyFineTuneFactor = (formula: Formula): number =>
  1 +
  (formula.fineTuneMultiplier - DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX) *
    formula.fineTuneMultiplierVal;

export const computeLaserTimeMs = (
  formula: Formula,
  radiusMm: number,
  options: LaserTimeOptions
): number => {
  const fineTuneFactor =
    options.formulaVersion === "legacy"
      ? getLegacyFineTuneFactor(formula)
      : getFineTuneFactor(
          options.fineTuneIndex ?? DEFAULT_SHOOT_TIME_FINE_TUNE_INDEX
        );
  const uncappedResult =
    fineTuneFactor *
    (formula.multiplier * radiusMm ** formula.exponent + formula.offset);
  return Math.min(uncappedResult, formula.maxTime);
};

/** Peak laser time per band, including each band's upper threshold (matches the drawn line). */
const getMaxLaserTimeMs = (
  sizes: readonly number[],
  formulas: readonly Formula[],
  maxRadiusMm: number,
  formulaVersion: ShootTimeFormulaVersion
): number => {
  const options: LaserTimeOptions =
    formulaVersion === "legacy"
      ? { formulaVersion }
      : { formulaVersion, fineTuneIndex: MAX_SHOOT_TIME_FINE_TUNE_INDEX };
  const boundsList = getBandBounds(sizes, maxRadiusMm);
  let maxLaserTime = 0;
  for (let bandIndex = 0; bandIndex < boundsList.length; bandIndex++) {
    const formula = formulas[bandIndex];
    const bounds = boundsList[bandIndex];
    if (!formula || !bounds) {
      continue;
    }
    for (let x = bounds.lower; x <= bounds.upper; x += DOMAIN_STEP_MM) {
      maxLaserTime = Math.max(
        maxLaserTime,
        computeLaserTimeMs(formula, x, options)
      );
    }
    maxLaserTime = Math.max(
      maxLaserTime,
      computeLaserTimeMs(formula, bounds.upper, options)
    );
  }
  return maxLaserTime || 1;
};

const THREE_BAND_LABEL_KEYS = [
  "utils.descriptors.small",
  "utils.descriptors.medium",
  "utils.descriptors.large",
] as const;

export const getBandLabel = (
  t: TFunction,
  bandIndex: number,
  bandCount: number
): string => {
  if (bandCount === 3 && bandIndex < THREE_BAND_LABEL_KEYS.length) {
    return t(THREE_BAND_LABEL_KEYS[bandIndex]!);
  }
  return t("views.categoryTemplates.shootTime.bandLabel", {
    n: bandIndex + 1,
  });
};

export interface ShootTimeBand {
  label: string;
  bounds: BandBounds;
  color: string;
  formula: Formula;
}

export type FormulaFieldKey = Exclude<
  keyof Formula,
  "fineTuneMultiplier" | "fineTuneMultiplierVal"
>;

export const MULTIPLIER_FORMULA_FIELD_KEY = "multiplier" as const;

/** Every tunable formula field, in display order. */
export const FORMULA_FIELD_KEYS = [
  MULTIPLIER_FORMULA_FIELD_KEY,
  "exponent",
  "maxTime",
  "offset",
] as const satisfies ReadonlyArray<FormulaFieldKey>;

/**
 * Everything but the multiplier, which is tuned per size band. These are the
 * fields behind the advanced accordion and the ones bands can share.
 */
export const ADVANCED_FIELD_KEYS: readonly FormulaFieldKey[] =
  FORMULA_FIELD_KEYS.filter((key) => key !== MULTIPLIER_FORMULA_FIELD_KEY);

const TIME_FIELDS = new Set<FormulaFieldKey>(["maxTime", "offset"]);

export const formatFormulaFieldValue = (
  t: TFunction,
  key: FormulaFieldKey,
  value: number
): string => {
  if (TIME_FIELDS.has(key)) {
    return t("utils.units.valueWithUnit", {
      value,
      unit: t("utils.units.ms"),
    });
  }
  return String(value);
};

export const formulasShareAdvanced = (a: Formula, b: Formula): boolean =>
  ADVANCED_FIELD_KEYS.every((key) => a[key] === b[key]);

/** Returns the formula carrying the shared advanced fields, otherwise undefined. */
export const getSharedAdvancedFormula = (
  formulas: readonly Formula[]
): Formula | undefined => {
  const first = formulas[0];
  if (!first) {
    return undefined;
  }
  return formulas.every((formula) => formulasShareAdvanced(first, formula))
    ? first
    : undefined;
};

/** Category-library / plant-profile almanacs do not use FTI or FTM. */
export const withoutFineTuneFields = (
  formulas: readonly Formula[]
): Formula[] =>
  formulas.map((formula) => ({
    ...structuredClone(formula),
    fineTuneMultiplier: 0,
    fineTuneMultiplierVal: 0,
  }));

export const almanacWithoutFineTuneFields = (almanac: Almanac): Almanac => ({
  ...almanac,
  sizeBoundariesMm: [...almanac.sizeBoundariesMm],
  formulas: withoutFineTuneFields(almanac.formulas),
});

/**
 * Copies the first formula's advanced fields onto every formula, keeping each
 * band's own multiplier, and clears fine-tune fields that category-template /
 * plant-profile almanacs do not use.
 */
export const syncAdvancedFieldsToFirst = (
  formulas: readonly Formula[]
): Formula[] => {
  const first = formulas[0];
  if (!first) {
    return [];
  }
  return withoutFineTuneFields(
    formulas.map((formula) => ({
      ...structuredClone(first),
      [MULTIPLIER_FORMULA_FIELD_KEY]: formula[MULTIPLIER_FORMULA_FIELD_KEY],
    }))
  );
};

export const getShootTimeBands = (
  t: TFunction,
  sizes: readonly number[],
  formulas: readonly Formula[],
  colors: readonly string[],
  maxRadiusMm: number = getDefaultMaxRadiusMm(sizes)
): ShootTimeBand[] => {
  const boundsList = getBandBounds(sizes, maxRadiusMm);
  const bandCount = boundsList.length;
  const bands: ShootTimeBand[] = [];

  for (let index = 0; index < bandCount; index++) {
    const formula = formulas[index];
    const bounds = boundsList[index];
    const color = colors[index % colors.length];
    if (!formula || !bounds || !color) {
      continue;
    }
    bands.push({
      label: getBandLabel(t, index, bandCount),
      bounds,
      color,
      formula,
    });
  }

  return bands;
};

/** Threshold sizes between bands (excludes the final band's upper/max radius). */
export const getSizesFromBands = (bands: readonly ShootTimeBand[]): number[] =>
  bands.slice(0, -1).map((band) => band.bounds.upper);

export const getFormulasFromBands = (
  bands: readonly ShootTimeBand[]
): Formula[] => bands.map((band) => band.formula);

export const getMaxRadiusMmFromBands = (
  bands: readonly ShootTimeBand[]
): number => bands.at(-1)?.bounds.upper ?? DEFAULT_MAX_RADIUS_MM;

/** Unique Max Time caps across bands, ascending — used for Y reference lines. */
export const getBandMaxTimes = (bands: readonly ShootTimeBand[]): number[] => {
  const unique = new Set<number>();
  for (const band of bands) {
    const maxTime = band.formula.maxTime;
    if (Number.isFinite(maxTime) && maxTime > 0) {
      unique.add(maxTime);
    }
  }
  return [...unique].sort((a, b) => a - b);
};

export const getShootTimeDomain = (
  bands: readonly ShootTimeBand[],
  formulaVersion: ShootTimeFormulaVersion
): { maxRadiusMm: number; maxLaserTime: number } => {
  const sizes = getSizesFromBands(bands);
  const formulas = getFormulasFromBands(bands);
  const maxRadiusMm = getMaxRadiusMmFromBands(bands);
  const peakLaserTime = getMaxLaserTimeMs(
    sizes,
    formulas,
    maxRadiusMm,
    formulaVersion
  );
  const maxTimeCap = Math.max(0, ...getBandMaxTimes(bands));
  return {
    maxRadiusMm,
    maxLaserTime: Math.max(peakLaserTime, maxTimeCap, 1),
  };
};

/** Minimum gap (mm) between adjacent size thresholds while dragging/editing. */
export const THRESHOLD_MIN_GAP_MM = 1;

/** Allowed range for `sizes[index]` given neighbors and the chart domain end. */
export const getThresholdDragBounds = (
  sizes: readonly number[],
  index: number,
  maxRadiusMm: number
): { min: number; max: number } => {
  const leftNeighbor = index <= 0 ? 0 : sizes[index - 1]!;
  const rightNeighbor =
    index >= sizes.length - 1 ? maxRadiusMm : sizes[index + 1]!;
  return {
    min: leftNeighbor + THRESHOLD_MIN_GAP_MM,
    max: rightNeighbor - THRESHOLD_MIN_GAP_MM,
  };
};

/** Returns a new sizes array with `sizes[index]` clamped into its drag bounds. */
export const clampThresholdSize = (
  sizes: readonly number[],
  index: number,
  next: number,
  maxRadiusMm: number
): number[] => {
  const { min, max } = getThresholdDragBounds(sizes, index, maxRadiusMm);
  if (min > max) {
    return [...sizes];
  }
  const clamped = Math.min(max, Math.max(min, Math.round(next)));
  const result = [...sizes];
  result[index] = clamped;
  return result;
};

/** Convert an SVG x coordinate within the plot to a radius in mm. */
export const pxToRadiusMm = (
  xPx: number,
  plotLeft: number,
  plotWidth: number,
  maxRadiusMm: number
): number => {
  if (plotWidth <= 0) {
    return 0;
  }
  const ratio = (xPx - plotLeft) / plotWidth;
  return Math.round(Math.min(1, Math.max(0, ratio)) * maxRadiusMm);
};
